import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { TICKER_PLACES, DESTINATIONS } from '@/data/destinations'
import { fetchImages, type Img } from '@/services/images'
import { FallbackImage } from '@/components/shared/FallbackImage'
import { Atmosphere } from '@/components/shared/Atmosphere'

// Signature destinations shown as floating photo cards on the immersive hero.
const SIGNATURE = [
  { id: 'athens', name: 'Athens', flag: '🇬🇷', style: { top: '10%', left: '5%', rotate: -3 } },
  { id: 'kyoto', name: 'Kyoto', flag: '🇯🇵', style: { top: '6%', right: '6%', rotate: 2 } },
  { id: 'dubrovnik', name: 'Dubrovnik', flag: '🇭🇷', style: { top: '58%', left: '3%', rotate: 2 } },
  { id: 'serengeti', name: 'Serengeti', flag: '🇹🇿', style: { top: '62%', right: '4%', rotate: -2 } },
  { id: 'patagonia', name: 'Patagonia', flag: '🇦🇷', style: { top: '32%', left: '11%', rotate: -1 } },
]

function findDest(id: string) {
  for (const key of Object.keys(DESTINATIONS) as (keyof typeof DESTINATIONS)[]) {
    const found = DESTINATIONS[key].find((d) => d.id === id)
    if (found) return found
  }
  return null
}

export function WelcomeScreen() {
  const setScreen = useAppStore((s) => s.setScreen)
  const ticker = [...TICKER_PLACES, ...TICKER_PLACES]
  const [cardImages, setCardImages] = useState<Record<string, Img | null>>({})

  useEffect(() => {
    let cancelled = false
    SIGNATURE.forEach(async (sig) => {
      const dest = findDest(sig.id)
      if (!dest) return
      const images = await fetchImages(dest.imageQuery, 1)
      if (!cancelled) {
        setCardImages((prev) => ({ ...prev, [sig.id]: images[0] || null }))
      }
    })
    return () => { cancelled = true }
  }, [])

  const advance = () => setScreen('mode')

  return (
    <div
      onClick={advance}
      className="cross-pattern"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at 50% 30%, #3A2312 0%, #241209 55%, #180C05 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
      }}
    >
      <Atmosphere count={22} />

      {/* Vignette */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 40%, transparent 40%, rgba(0,0,0,0.5) 100%)', pointerEvents: 'none', zIndex: 1 }} />

      {/* Floating destination photo cards */}
      {SIGNATURE.map((sig, i) => {
        const img = cardImages[sig.id]
        if (!img) return null
        return (
          <motion.div
            key={sig.id}
            initial={{ opacity: 0, y: 14, rotate: sig.style.rotate }}
            animate={{ opacity: 1, y: [0, -8, 0], rotate: sig.style.rotate }}
            transition={{
              opacity: { delay: 0.15 * i, duration: 0.8, ease: 'easeOut' },
              y: { delay: 0.8 + 0.15 * i, duration: 6 + i * 0.4, repeat: Infinity, ease: 'easeInOut' },
            }}
            style={{
              position: 'absolute',
              top: sig.style.top,
              left: 'left' in sig.style ? sig.style.left : undefined,
              right: 'right' in sig.style ? sig.style.right : undefined,
              width: 130,
              zIndex: 2,
              display: 'none',
            }}
            className="p-hero-card"
          >
            <div className="p-glow-card" style={{ border: '2px solid rgba(212,160,23,0.4)', background: '#1A0F08', overflow: 'hidden' }}>
              <FallbackImage
                src={img.thumb || img.url}
                alt={img.alt || sig.name}
                style={{ height: 84, width: '100%' }}
                fallback={<div />}
              />
              <div style={{ padding: '5px 8px', fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.08em', color: 'var(--gold2)', textTransform: 'uppercase' }}>
                {sig.flag} {sig.name}
              </div>
            </div>
          </motion.div>
        )
      })}

      {/* Centre content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 40px', position: 'relative', zIndex: 3, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            border: '1px solid rgba(212,160,23,0.4)', padding: '5px 16px',
            fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.18em',
            color: 'var(--gold2)', marginBottom: 24, textTransform: 'uppercase',
          }}
        >
          ✦ by Imaginarium · For the Experienced Traveller
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
          style={{
            fontFamily: 'var(--serif-display)', fontSize: 'clamp(38px, 8vw, 68px)', fontWeight: 600,
            color: '#F5EDD8', lineHeight: 1.12, marginBottom: 16, maxWidth: 780,
          }}
        >
          Your greatest journeys<br />
          <em style={{ color: 'var(--gold2)', textShadow: '0 0 30px rgba(212,160,23,0.35)' }}>still lie ahead.</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.35 }}
          style={{
            fontFamily: 'var(--serif-body)', fontSize: 'clamp(15px, 2vw, 19px)', fontStyle: 'italic',
            color: 'rgba(245,237,216,0.72)', lineHeight: 1.7, marginBottom: 40, maxWidth: 520,
          }}
        >
          Five questions. A lifetime of taste. <span style={{ color: 'var(--gold2)' }}>Your</span> most meaningful trip yet.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55 }}
          className="p-pulse-pill"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            border: '1px solid rgba(212,160,23,0.55)', borderRadius: 999,
            padding: '12px 30px', background: 'rgba(212,160,23,0.06)',
          }}
        >
          <span style={{ color: 'var(--gold)', fontSize: 10 }}>●</span>
          <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.2em', color: 'var(--gold2)', textTransform: 'uppercase' }}>
            Touch anywhere to begin
          </span>
          <span style={{ color: 'var(--gold)', fontSize: 10 }}>●</span>
        </motion.div>
      </div>

      {/* Ticker */}
      <div style={{ background: 'rgba(0,0,0,0.35)', borderTop: '1px solid rgba(212,160,23,0.15)', padding: '10px 0', overflow: 'hidden', position: 'relative', zIndex: 3 }}>
        <div className="p-ticker-animate" style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          {ticker.map((place, i) => (
            <span key={i} style={{ fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.14em', color: 'var(--warm)', padding: '0 22px', textTransform: 'uppercase' }}>
              {place} <span style={{ opacity: 0.35, color: 'var(--gold)' }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @media (min-width: 700px) {
          .p-hero-card { display: block !important; }
        }
      `}</style>
    </div>
  )
}
